import * as Yup from "yup";
import { decimalOnly } from "../../../component/form/Validation";
import moment from "moment";
import { normalizeResourceRequirements } from "./serviceResourceRequirements";

const integerIds = (value) => (Array.isArray(value) ? value.flat(Infinity) : [value])
  .filter((item) => item !== null && item !== undefined && /^\d+$/.test(String(item)))
  .map((item) => Number(item));

const addonServiceRelations = (value, durations = {}) => (Array.isArray(value) ? value.flat(Infinity) : [value])
  .filter((item) => item !== null && item !== undefined && item !== '')
  .map((item) => {
    if (!item || typeof item !== 'object') return Number(item);
    const serviceId = Number(item.service_id || item.id || 0);
    const duration = item.duration === '' || item.duration === null
      || item.duration === undefined
      ? null
      : Number(item.duration);
    return { service_id: serviceId, duration };
  })
  .map((item) => {
    if (typeof item !== 'number') return item;
    return { service_id: item, duration: durations[item] ?? null };
  })
  .filter((item) => typeof item === 'number' || item.service_id > 0);

const hasText = (value) => value !== null
  && value !== undefined
  && String(value).trim() !== '';

const isEnabledFlag = (value) => value === true
  || value === 1
  || ['1', 'true', 'yes', 'on'].includes(String(value).trim().toLowerCase());

export const serviceDepositFormState = (detail = {}) => ({
  deposit_booked_online: isEnabledFlag(detail.deposit_booked_online) ? 1 : '',
  deposit_booked_price: hasText(detail.deposit_booked_price)
    ? String(detail.deposit_booked_price)
    : '',
});

const normalizeServiceDeposit = (values = {}) => {
  const enabled = isEnabledFlag(values.deposit_booked_online);
  return {
    deposit_booked_online: enabled ? 1 : 0,
    deposit_booked_price: enabled && hasText(values.deposit_booked_price)
      ? values.deposit_booked_price
      : 0,
  };
};

export const hasServicePrice = (prices) => Array.isArray(prices)
  && prices.some((item) => item && hasText(item.price));

// Calendar increments describe how appointments are displayed, not how long a
// service may run. Durations therefore use a five minute floor so a service can
// be shorter than the increment (for example 5 minutes on a 15 minute calendar).
export const serviceDurationCadenceMinutes = (slotTime) => {
  const slot = Number(slotTime);
  if (!Number.isFinite(slot) || slot <= 0) return 5;
  return Math.min(Math.floor(slot), 5);
};

export const serviceDurationOptions = (slotTime) => {
  const cadence = serviceDurationCadenceMinutes(slotTime);
  const options = [];
  for (let minutes = cadence; minutes < 24 * 60; minutes += cadence) {
    const label = `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
    options.push({ value: minutes, label });
  }
  return options;
};

const optionalDecimal = (value) => !hasText(value) || decimalOnly(value);

export const servicePriceValidationSchema = (t) => Yup.array().of(
  Yup.object().shape({
    price_tier_id: Yup.lazy((value) => (Array.isArray(value)
      ? Yup.array().of(Yup.string()).nullable().min(1).required(t("Required"))
      : Yup.string().nullable().label(t("Price Tier")).required())),
    price: Yup.string().trim().nullable().label(t("Price"))
      .test("Decimal only", t("The field should have decimal only"), optionalDecimal),
    add_on_price: Yup.string().trim().nullable().label(t("Add-on Price"))
      .test("Decimal only", t("The field should have decimal only"), optionalDecimal),
    duration: Yup.number()
      .transform((value, originalValue) => (hasText(originalValue) ? Number(originalValue) : null))
      .nullable()
      .integer(t("Duration must be a positive whole number"))
      .positive(t("Duration must be a positive whole number"))
      .typeError(t("Duration must be a positive whole number")),
  }),
).test("at-least-one-service-price", t("Required"), hasServicePrice);

const normalizeTierDuration = (duration) => (hasText(duration) ? Number(duration) : null);

export const normalizeServicePrices = (prices) => (Array.isArray(prices) ? prices : [])
  .filter((item) => item && hasText(item.price))
  .map((item) => ({ ...item, duration: normalizeTierDuration(item.duration) }));

export const normalizePriceTierServiceDurations = (values = {}) => ({
  ...values,
  service_price_tier: (Array.isArray(values.service_price_tier) ? values.service_price_tier : [])
    .map((item) => ({ ...item, duration: normalizeTierDuration(item.duration) })),
});

export const normalizeServiceIntakeQuestions = (values = {}) => {
  const enabled = isEnabledFlag(values.requires_intake_form);
  const questions = (Array.isArray(values.intake_questions) ? values.intake_questions : [])
    .filter((question) => question && hasText(question.question))
    .filter((question) => hasText(question.type))
    .map((question) => ({
      question: String(question.question).trim(),
      type: String(question.type).trim(),
      is_required: Boolean(question.is_required),
      options: (Array.isArray(question.options) ? question.options : [])
        .map((option) => String(option == null ? "" : option).trim())
        .filter((option) => option !== ""),
      blocking_answer: hasText(question.blocking_answer)
        ? String(question.blocking_answer).trim()
        : "",
    }));
  const effective = enabled ? questions : [];
  return {
    requires_intake_form: effective.length > 0 ? "1" : "0",
    intake_questions: effective,
  };
};

export const normalizeServiceRelations = (values = {}) => {
  const resourceRequirements = normalizeResourceRequirements(values.resource_requirements);
  const serviceValues = { ...values };
  delete serviceValues.color;
  return {
    ...serviceValues,
    ...(Object.prototype.hasOwnProperty.call(values, 'service_booked_online')
      ? { service_booked_online: isEnabledFlag(values.service_booked_online) ? 1 : 0 }
      : {}),
    ...(Object.prototype.hasOwnProperty.call(values, 'hide_service_booking_widget')
      ? { hide_service_booking_widget: isEnabledFlag(values.hide_service_booking_widget) ? 1 : 0 }
      : {}),
    ...normalizeServiceDeposit(values),
    resource_requirements: resourceRequirements,
    // Keep the first row for old servers and clients during the additive rollout.
    resource_id: resourceRequirements[0]?.resource_id ?? (values.resource_id || ""),
    add_on_within_original_time: Boolean(values.add_on_within_original_time),
    service_price: normalizeServicePrices(values.service_price),
    add_on_services: addonServiceRelations(values.add_on_services, values.add_on_service_durations),
    add_on_staff: integerIds(values.add_on_staff),
    staff_services: integerIds(values.staff_services ?? values.add_on_staff),
    add_on_category: integerIds(values.add_on_category),
    add_on_price_tier: integerIds(values.add_on_price_tier),
    ...normalizeServiceIntakeQuestions(values),
  };
};

export const totalServiceDurationMinutes = ({
  durationWithPadding = '',
  addonservices = [],
  addanotherservices = [],
  add_on_within_original_time = false,
} = {}) => {
  const parseMinutes = (value) => {
    const duration = moment.duration(value || '');
    return duration.isValid() ? duration.asMinutes() : 0;
  };

  const base = parseMinutes(durationWithPadding);
  const contained = add_on_within_original_time
    ? 0
    : addonservices.reduce((sum, item) => sum + parseMinutes(item?.durationWithPadding), 0);
  const added = addanotherservices.reduce((sum, item) => sum + parseMinutes(item?.durationWithPadding), 0);
  const totalMinutes = base + contained + added;

  return {
    totalHours: totalMinutes > 0
      ? moment.utc(totalMinutes * 60000).format('HH:mm')
      : '',
    totalMinutes: totalMinutes > 0 ? totalMinutes : '',
  };
};
